import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { ShieldAlert, LogOut } from "lucide-react";

/**
 * Dvojni UI element:
 *  1. Za SA-ja v načinu impersonacije: oranžna obroba okrog celotnega displeja + heartbeat na strežnik.
 *  2. Za NAVADNEGA uporabnika (predsednika LD): banner, ko SA trenutno impersonira njegovo LD.
 *
 * Uporablja endpoint `/api/impersonation/check` za polling in `/api/superadmin/impersonate/ping`
 * za SA heartbeat.
 */
const POLL_INTERVAL_MS = 20_000;  // uporabnik strani preveri stanje na 20 s
const PING_INTERVAL_MS = 60_000;  // SA session pinga strežnik na 60 s

export const SAImpersonationOverlay = () => {
  const { user, token, returnFromImpersonate } = useAuth();
  const navigate = useNavigate();
  const [saActive, setSaActive] = useState(null);  // {active, sa_ime, started_at} za navadnega usera
  const [exiting, setExiting] = useState(false);
  const pingRef = useRef(null);
  const pollRef = useRef(null);

  const isSAImpersonating = !!user?.impersonating;
  const isRegularUser = user && !isSAImpersonating && user.vloga !== "super_admin";

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      const ok = await returnFromImpersonate();
      if (ok) navigate("/super-admin");
    } finally {
      setExiting(false);
    }
  };

  // ---- SA heartbeat ----
  useEffect(() => {
    if (!isSAImpersonating || !token) return;
    const doPing = async () => {
      try {
        await axios.post(`${API}/superadmin/impersonate/ping`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* prezri – morda smo pravkar zapustili impersonacijo */ }
    };
    doPing();  // takoj prvi ping ob mount-u
    pingRef.current = setInterval(doPing, PING_INTERVAL_MS);
    return () => clearInterval(pingRef.current);
  }, [isSAImpersonating, token]);

  // ---- Navadni user polling ----
  useEffect(() => {
    if (!isRegularUser || !token) { setSaActive(null); return; }
    const doPoll = async () => {
      try {
        const res = await axios.get(`${API}/impersonation/check`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSaActive(res.data?.active ? res.data : null);
      } catch {
        setSaActive(null);
      }
    };
    doPoll();
    pollRef.current = setInterval(doPoll, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [isRegularUser, token]);

  return (
    <>
      {/* Oranžna obroba + Exit gumb za SA */}
      {isSAImpersonating && (
        <>
          <div
            className="pointer-events-none fixed inset-0 z-[9999]"
            style={{ boxShadow: "inset 0 0 0 6px rgb(249 115 22)" }}
            data-testid="sa-impersonation-border"
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={handleExit}
            disabled={exiting}
            className="fixed top-3 right-3 z-[10001] inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-medium px-3 py-2 rounded-full shadow-lg transition"
            data-testid="sa-exit-btn"
            title="Izhod iz SA načina"
          >
            <LogOut className="h-4 w-4" />
            {exiting ? "Odjavljam ..." : "Izhod iz SA načina"}
          </button>
        </>
      )}
      {/* Banner za navadnega uporabnika */}
      {saActive?.active && (
        <div
          className="fixed top-0 left-0 right-0 z-[10000] bg-amber-500 text-white shadow-md"
          data-testid="user-sa-active-banner"
          role="alert"
        >
          <div className="flex items-center gap-2 px-4 py-2 text-sm">
            <ShieldAlert className="h-5 w-5 flex-shrink-0" />
            <div className="flex-1">
              <b>Super-admin je trenutno prijavljen kot vi.</b>{" "}
              <span className="opacity-90">
                {saActive.sa_ime} je začel sejo ob{" "}
                {saActive.started_at ? new Date(saActive.started_at).toLocaleString("sl-SI") : "?"}.
                Vsa dejanja se beležijo.
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SAImpersonationOverlay;
